import "./CompletedOrders.css";
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useIp } from "../context/IpContext";
import { useSocket } from "../context/SocketContext";

export default function CompletedOrders() {
  const { type } = useParams();
  const [completedOrders, setCompletedOrders] = useState([]);
  const { socket } = useSocket();
  const navigate = useNavigate();
  const { ipAddress, port } = useIp();

  useEffect(() => {
    if (!ipAddress) return;

    const url = `http://${ipAddress}:${port}/api/${type}/completed-orders`;

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch completed orders");
        return res.json();
      })
      .then((data) => {
        setCompletedOrders(data);
      })
      .catch((err) => {
        console.error("Error fetching completed orders:", err);
      });
  }, [ipAddress, type]);

  useEffect(() => {
    if (!socket) return;

    socket.emit("join_room", type);

    const handleOrderReactivated = (order) => {
      setCompletedOrders((prev) => prev.filter((o) => o.uuid !== order.uuid));
    };

    const handleCompletedOrder = (order) => {
      setCompletedOrders((prev) => {
        if (prev.some((o) => o.uuid === order.uuid)) return prev;
        return [order, ...prev];
      });
    };

    socket.on("reactivate_order", handleOrderReactivated);
    socket.on("order_completed", handleCompletedOrder);

    return () => {
      socket.off("reactivate_order", handleOrderReactivated);
      socket.off("order_completed", handleCompletedOrder);
    };
  }, [socket]);

  const reactivateOrder = async (uuid) => {
    try {
      await fetch(`http://${ipAddress}:${port}/api/reactivate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uuid }),
      });
      setCompletedOrders((prev) => prev.filter((order) => order.uuid !== uuid));
    } catch (err) {
      console.error("Failed to reactivate order:", err);
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <div className="completed-orders-container">
        {completedOrders.length === 0 && (
          <div className="no-completed-state">No Completed Orders</div>
        )}

        {completedOrders.map((order) => (
          <div
            className="completed-order"
            key={order.uuid}
            onClick={() => reactivateOrder(order.uuid)}
          >
            {/* Header */}
            <div
              className={`completed-order-header ${order.type === "takeaway" ? "takeaway" : "dinein"}`}
            >
              <div className="completed-order-number">
                Order #{order.order_name}
              </div>
              <div className="completed-order-meta-row">
                <span>{formatTime(order.created_at)}</span>
                <span className="completed-order-type-label">
                  {order.type === "takeaway" ? "Takeaway" : "Dine-In"}
                </span>
              </div>
            </div>

            {/* Items */}
            <div className="completed-order-items">
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {order.items?.map((item, i) => (
                  <li
                    key={`${order.uuid}-${i}-${item.name}`}
                    style={{
                      fontWeight: "bold",
                      fontSize: "100%",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span className={`item-qty ${Number(item.quantity) === 1 ? "qty-default" : "qty-warning"}`}>
                      {item.quantity}
                    </span>
                    <span>{item.name}</span>

                    {(item.modifiers || item.variation_name || item.note) && (
                      <ul
                        style={{
                          listStyle: "none",
                          fontSize: "0.9em",
                          marginTop: "4px",
                          marginLeft: "1rem",
                          color: "#555",
                          fontWeight: "normal",
                        }}
                      >
                        {item.note && item.note.trim() !== "" && (
                          <li style={{ color: "red", fontWeight: "bold" }}>
                            NOTE: {item.note}
                          </li>
                        )}
                        {item.variation_name &&
                          item.variation_name.trim() !== "" && (
                            <li>+ {item.variation_name}</li>
                          )}
                        {item.modifiers &&
                          item.modifiers.map((mod) => (
                            <li key={`${item.uid}-${mod.name}`}>
                              + {mod.name}
                            </li>
                          ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>

      <button
        className="back-button"
        onClick={() => navigate(`/active/${type}`)}
      >
        ← Active Orders
      </button>
    </div>
  );
}

function formatTime(timestamp) {
  const date = new Date(timestamp);
  return date
    .toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    })
    .toUpperCase();
}
